import { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEO from '../components/SEO';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  title: string;
  items: FaqItem[];
}

const faqData: FaqCategory[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is Supaserver?',
        answer: 'Supaserver is a no-code backend platform that lets you create REST APIs in seconds. Define your endpoints, store data, and go live instantly — no servers, no DevOps, no coding required. Perfect for MVPs, prototypes, and production applications.'
      },
      {
        question: 'How do I create my first API?',
        answer: 'Simply sign up for a free account, open the API builder, and define your endpoints. You can specify the HTTP methods (GET, POST, PUT, DELETE), set up your data schema, and your API is ready to use immediately. We provide code snippets for curl, JavaScript, Python, and more.'
      },
      {
        question: 'Do I need coding experience to use Supaserver?',
        answer: 'No coding experience is required! Supaserver is designed to be user-friendly for everyone — from non-technical founders to experienced developers who want to speed up their workflow. Our visual interface makes API creation as simple as filling out a form.'
      },
      {
        question: 'Can I try Supaserver for free?',
        answer: 'Yes! We offer a generous free tier that includes up to 3 collections, 1,000 API requests per month, and 100MB of data storage. It\'s perfect for learning, testing, and small projects. No credit card required to get started.'
      }
    ]
  },
  {
    title: 'Features & Capabilities',
    items: [
      {
        question: 'What HTTP methods are supported?',
        answer: 'The free plan supports basic methods: GET, POST, PUT, and DELETE. Pro and Enterprise plans unlock all HTTP methods including PATCH, HEAD, and OPTIONS for complete RESTful API functionality.'
      },
      {
        question: 'How does data storage work?',
        answer: 'Supaserver uses MongoDB under the hood to store your data securely. Each collection you create automatically gets full CRUD operations. Your data is persisted and accessible via your API endpoints 24/7.'
      },
      {
        question: 'Can I use Supaserver for production applications?',
        answer: 'Absolutely! Many customers use Supaserver for production apps. Our Pro plan offers unlimited API requests, 10GB storage, advanced authentication, and priority support — everything you need for production workloads.'
      },
      {
        question: 'Does Supaserver provide authentication?',
        answer: 'Yes! We provide API key authentication to secure your endpoints. Pro users get advanced authentication options including JWT support. Enterprise customers can integrate with their existing identity providers.'
      },
      {
        question: 'What code snippets are provided?',
        answer: 'We auto-generate ready-to-use code snippets for curl, JavaScript (fetch), Node.js (axios), and Python (requests). Just copy and paste into your application to start making API calls immediately.'
      }
    ]
  },
  {
    title: 'Use Cases',
    items: [
      {
        question: 'Can I use Supaserver for my mobile app backend?',
        answer: 'Yes! Supaserver is perfect for mobile app backends. Whether you\'re building with React Native, Flutter, Swift, or Kotlin, our REST APIs work seamlessly. Many indie developers use Supaserver to power their iOS and Android apps.'
      },
      {
        question: 'Is Supaserver good for prototyping and MVPs?',
        answer: 'Supaserver is ideal for prototyping and MVPs. Instead of spending weeks setting up servers and databases, you can have a working backend in minutes. Focus on validating your idea, not infrastructure.'
      },
      {
        question: 'Can I use Supaserver with no-code tools like Bubble or FlutterFlow?',
        answer: 'Yes! Supaserver integrates perfectly with no-code and low-code platforms. Use our REST API endpoints directly in Bubble, FlutterFlow, Webflow, or any tool that supports HTTP requests.'
      },
      {
        question: 'Is Supaserver suitable for hackathons?',
        answer: 'Supaserver is a hackathon secret weapon! When time is critical, skip the backend setup entirely. Many hackathon winners have used Supaserver to ship faster and focus on their core product.'
      }
    ]
  },
  {
    title: 'Technical Questions',
    items: [
      {
        question: 'What is the API rate limit?',
        answer: 'Free plans have a limit of 1,000 API requests per month. Pro plans have no rate limits — use as many requests as you need. We implement fair-use policies to ensure platform stability for all users.'
      },
      {
        question: 'Where is my data hosted?',
        answer: 'Your data is hosted on secure cloud infrastructure with automatic backups. We take security seriously and implement industry-standard practices to protect your data.'
      },
      {
        question: 'Can I export my data?',
        answer: 'Yes, you can export your data at any time. We believe in data portability — your data belongs to you. Pro and Enterprise users have access to advanced export options.'
      },
      {
        question: 'Does Supaserver support CORS?',
        answer: 'Yes, CORS is enabled by default so you can call your APIs from any domain. This makes Supaserver perfect for frontend applications, mobile apps, and third-party integrations.'
      },
      {
        question: 'What happens if I exceed my plan limits?',
        answer: 'We\'ll notify you when you\'re approaching your limits. If you exceed them, your API will continue working but with reduced performance. We recommend upgrading to Pro for unlimited usage.'
      }
    ]
  },
  {
    title: 'Comparisons',
    items: [
      {
        question: 'How is Supaserver different from Firebase?',
        answer: 'While Firebase is a full-featured platform with a steep learning curve, Supaserver focuses specifically on instant REST API creation. No SDK integration, no complex setup — just define your endpoints and go. Perfect when you need a simple, fast backend solution.'
      },
      {
        question: 'Supaserver vs Supabase — what\'s the difference?',
        answer: 'Supabase is a Firebase alternative with PostgreSQL. Supaserver is designed for instant API creation with zero configuration. If you need a database-first approach with SQL, choose Supabase. If you want instant REST endpoints without any setup, choose Supaserver.'
      },
      {
        question: 'How does Supaserver compare to Xano?',
        answer: 'Xano is a powerful no-code backend with visual programming and complex business logic. Supaserver is simpler and faster — focused purely on instant REST API creation. If you need advanced workflows and custom logic, Xano is great. If you want the fastest path to a working API with minimal setup, Supaserver wins.'
      },
      {
        question: 'Why choose Supaserver over building my own backend?',
        answer: 'Building a backend from scratch takes weeks: server setup, database configuration, API design, authentication, deployment, and maintenance. With Supaserver, you get all of this in seconds. Save time and focus on what matters — your product.'
      }
    ]
  }
];

function FaqItem({ question, answer, isOpen, onToggle, isLast }: { 
  question: string; 
  answer: string; 
  isOpen: boolean; 
  onToggle: () => void;
  isLast: boolean;
}) {
  return (
    <div className={isLast ? '' : 'border-b border-gray-200'}>
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between text-left cursor-pointer"
      >
        <span className="text-lg font-medium text-gray-900 pr-8">{question}</span>
        <svg
          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p className="text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

function FaqCategory({ category }: { category: FaqCategory }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{category.title}</h2>
      <div className="bg-white rounded-xl border border-gray-200 px-6">
        {category.items.map((item, index) => (
          <FaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            isLast={index === category.items.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export default function FaqPage() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SEO 
        title="FAQ — Supaserver | Create Backend API Without Code"
        description="Frequently asked questions about Supaserver. Learn how to create REST APIs without code, pricing plans, features, use cases, and how Supaserver compares to Firebase and Supabase."
        canonical="https://supaserver.app/faq"
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-3xl mx-auto text-center relative z-10">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/20 rounded-2xl mb-8">
              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Frequently Asked{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                Questions
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about Supaserver. Can't find what you're looking for?
            </p>
            
            <a 
              href="mailto:dimasauchanka@gmail.com" 
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-medium transition-all duration-200 border border-white/20 hover:border-white/30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Support
            </a>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            {faqData.map((category, index) => (
              <FaqCategory key={index} category={category} />
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to build your API?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join thousands of developers who ship faster with Supaserver.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="px-8 py-3 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors"
              >
                Start for Free
              </Link>
              <Link
                to="/pricing"
                className="px-8 py-3 bg-white text-gray-900 rounded-full font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

